import { useState } from "react";
import { HiOutlineChevronDown, HiOutlineSearch } from "react-icons/hi";
import { LuBot } from "react-icons/lu";
import { auditActorKey, formatActorName } from "../../utils";

const MAX_CHIPS = 6;

const isBot = (actor) => actor.kind === "system" || actor.kind === "apikey";
const displayName = (actor) => (isBot(actor) ? "Robot" : formatActorName(actor));

const initialsOf = (actor) => {
  if (actor.kind === "patient") return "P";
  const parts = formatActorName(actor)
    .split(/[\s.\-_]+/)
    .filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0]?.slice(0, 2) || "?").toUpperCase();
};

const avatarClass = (actor, active) => {
  if (active) return "bg-white/20 text-white";
  if (isBot(actor)) return "bg-gray-100 text-gray-500";
  if (actor.kind === "patient") return "bg-primary-50 text-primary-700";
  return "bg-gray-900 text-white";
};

const ActorChips = ({ actors, value, onChange, allActors = [] }) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const visible = actors.slice(0, MAX_CHIPS);
  const selected = value && !visible.some((actor) => auditActorKey(actor) === value) && [...actors, ...allActors].find((actor) => auditActorKey(actor) === value);
  const pickable = [...new Map(allActors.map((actor) => [auditActorKey(actor), actor])).values()].filter((actor) =>
    displayName(actor).toLowerCase().includes(search.trim().toLowerCase()),
  );

  const chip = (actor) => {
    const key = auditActorKey(actor);
    const active = value === key;
    return (
      <button
        key={key}
        type="button"
        title={actor.label}
        onClick={() => onChange(active ? "" : key)}
        className={`flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2.5 text-[12px] font-medium transition-colors ${
          active ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ${avatarClass(actor, active)}`}>
          {isBot(actor) ? <LuBot className="h-3 w-3" /> : initialsOf(actor)}
        </span>
        {displayName(actor)}
      </button>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map(chip)}
      {selected && chip(selected)}
      {allActors.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsPickerOpen((open) => !open)}
            className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 py-0.5 pl-2.5 pr-2 text-[12px] font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
          >
            Autres
            <HiOutlineChevronDown className="h-3 w-3" />
          </button>
          {isPickerOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsPickerOpen(false)} />
              <div className="dropdown-panel absolute left-0 top-full z-20 mt-1 w-72">
                <div className="relative border-b border-gray-100 p-2">
                  <HiOutlineSearch className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input autoFocus type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un intervenant..." className="ghost-input pl-8" />
                </div>
                <div className="max-h-64 overflow-y-auto py-1">
                  {pickable.length === 0 && <div className="px-3 py-4 text-center text-xs text-gray-400">Aucun intervenant</div>}
                  {pickable.map((actor) => (
                    <button
                      key={auditActorKey(actor)}
                      type="button"
                      onClick={() => {
                        onChange(auditActorKey(actor));
                        setIsPickerOpen(false);
                        setSearch("");
                      }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold ${avatarClass(actor, false)}`}>
                        {isBot(actor) ? <LuBot className="h-3 w-3" /> : initialsOf(actor)}
                      </span>
                      <span className="truncate">{displayName(actor)}</span>
                      {actor.kind === "clinician" && <span className="meta-badge bg-gray-100 text-gray-500">IDE</span>}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActorChips;
